import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { registerStockNotification } from '../../fetchers/stock';
import { useAlert } from '../../context/AlertProvider';
import { IGlobalState } from '../../context/reducer';
import { getToken } from '../../helpers/auth';
import { getLangCountry } from '../../helpers/languages';
import BlueButtonBig from '../BlueButtonBig';
import FormErrorMessage from '../FormErrorMessage';
import FormInput from '../FormInput';
import Spinner from '../Spinner';

interface IStockNotificationForm {
  email: string;
}

interface IStockNotificationFormProps {
  sku: string;
  size?: string;
}

const StockNotificationForm: React.FC<IStockNotificationFormProps> = ({
  sku,
  size
}) => {
  const { profile: { email } = {} } = useSelector<IGlobalState, IGlobalState>(
    state => state || {}
  );
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [subscribedToRestock, setSubscribedToRestock] = useState(false);

  const alert = useAlert();
  const token = getToken();
  const router = useRouter();
  const { lang, country } = getLangCountry(router.query);

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    control
  } = useForm({
    defaultValues: {
      email: ''
    }
  });

  async function onSubmit({ email }: IStockNotificationForm) {
    setLoading(true);

    const { error } = await registerStockNotification({
      lang,
      country,
      token,
      sku,
      email,
      url: window.location.href
    });

    setLoading(false);

    if (error) {
      return alert.error(t('GENERIC_ERROR'));
    }

    setSubscribedToRestock(true);
  }

  // if user is logged in, set default email
  useEffect(() => {
    if (email) {
      setValue('email', email, { shouldValidate: true });
    }
  }, [email]);

  useEffect(() => {
    setSubscribedToRestock(false);
  }, [size]);

  return (
    <div className="bg-white rounded-md p-4 mt-4">
      {subscribedToRestock ? (
        <div className="space-y-1">
          <p className="font-bold text-gray-900">
            {t('RESTOCK.COMPLETED.TITLE')}
          </p>
          <p className="text-gray-400">{t('RESTOCK.COMPLETED.SUBTITLE')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-1">
            <p className="font-bold text-gray-900">{t('RESTOCK.FORM.TITLE')}</p>
            <p className="text-gray-400 text-sm">
              {t('RESTOCK.FORM.SUBTITLE')}
            </p>
          </div>

          <div>
            <Controller
              name="email"
              control={control}
              rules={{
                required: t('REQUIRED_FIELD') as string,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                  message: t('INVALID_EMAIL')
                }
              }}
              render={({ field }) => (
                <FormInput
                  id="email"
                  type="email"
                  label="Email"
                  placeholder={t('MAIL_ADDRESS')}
                  {...field}
                />
              )}
            />

            <FormErrorMessage errors={errors} name="email" />
          </div>

          <div className="mt-3">
            {loading ? (
              <Spinner />
            ) : (
              <BlueButtonBig
                text={t('RESTOCK.FORM.SUBSCRIBE')}
                onClick={() => {}}
              />
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default StockNotificationForm;
